import { ignoreChangesOf } from "./issue-ignore-ops.ts";
import { doorIntentOf } from "./door-ops.ts";
import { emptyMapOf, layoutIntentOf } from "./layout-read.ts";
import { patchOf } from "./op-read-patch.ts";
import { changeTilesetsOf } from "./tileset-ops.ts";
import { behaviourOf, footprintOf, emptyLayerOf, entityOf, groupOf, hasOnly, isFaceHeight, isId, isKey, isName, isOpId, isSortPixels, isText, keyChangesOf, pointOf, propOf, recordOf, tileOf, type Fields } from "./op-read.ts";
import { OP_CELLS_MAX, OP_FRAME_MAX, OP_INTENTS_MAX, type Intent, type LiveOp } from "./op-types.ts";

/**
 * The operation schema's codec (ADR-086 decisions 18 to 20): `encodeOp` writes an operation as its frame and
 * `parseOp` reads one, exact as the live channel's parsers are (`protocol.ts`): an operation is accepted only
 * with exactly its fields, each of its type and within its bounds, at most `OP_FRAME_MAX` code units as
 * `encodeOp` writes it and 1 to `OP_INTENTS_MAX` intents, so neither end acts on anything else. An operation carries no before-value: what
 * it replaces is read from the document where it is resolved.
 */

export { INTENT_KINDS, LIVE_OPS_VERSION, OP_CELLS_MAX, OP_CELL_END_MAX, OP_FRAME_MAX, OP_INTENTS_MAX, OP_JSON_DEPTH_MAX, OP_MAP_SIDE_MAX, OP_NAME_MAX, OP_TEXT_MAX, OP_TILESET_IDS_MAX, PATCH_CELL_RUNS_MAX, PATCH_GRIDS, PATCH_PALETTE_MAX, PATCH_VALUE_RUNS_MAX } from "./op-types.ts";
export type { CellPatchIntent, CellRun, EmptyLayer, EmptyMap, Intent, IntentOf, KeyChanges, LiveOp, OpPoint, PatchGrid, PatchValue, TilesetTiles, ValueRun } from "./op-types.ts";

/**
 * Whether a value is an id or null: a neighbour, a band's first layer or a layer's group, null meaning none.
 *
 * @param value The value.
 * @returns True for null or an id by the document id rule.
 */
function isIdOrNull(value: unknown): value is string | null {
  return value === null || isId(value);
}

/**
 * Reads a prop update: the prop and only the fields that changed. A footprint of the prop's own (ADR-040
 * amendment of 2026-10-04) is checked as a command checks it, names the frame its cells are of in the same
 * intent, and never comes with a behaviour; `null` clears it.
 *
 * @param fields The intent's fields.
 * @returns The intent, or undefined when it names no field or one is not of its type.
 */
function updatePropOf(fields: Fields): Intent | undefined {
  const optional = ["frame", "at", "flipX", "flipY", "sortPixels", "behaviour", "keepPlacement", "footprint"];
  const { map, layer, prop, flipX, flipY, sortPixels, keepPlacement } = fields;
  if (!hasOnly(fields, ["k", "map", "layer", "prop"], optional) || !optional.some((key) => Object.hasOwn(fields, key)) || !isId(map) || !isId(layer) || !isId(prop)) {
    return undefined;
  }
  const frame = fields["frame"] === undefined ? null : tileOf(fields["frame"], false);
  const at = fields["at"] === undefined ? null : pointOf(fields["at"]);
  const behaviour = fields["behaviour"] === undefined ? undefined : fields["behaviour"] === null ? null : (behaviourOf(fields["behaviour"]) ?? false);
  const footprint = fields["footprint"] === undefined ? undefined : fields["footprint"] === null ? null : (footprintOf(fields["footprint"]) ?? false);
  if (footprint === false || (footprint !== undefined && footprint !== null && (frame === null || (behaviour !== undefined && behaviour !== null)))) {
    return undefined;
  }
  if (frame === undefined || at === undefined || behaviour === false || (flipX !== undefined && typeof flipX !== "boolean") || (flipY !== undefined && typeof flipY !== "boolean") || (sortPixels !== undefined && !isSortPixels(sortPixels)) || (keepPlacement !== undefined && typeof keepPlacement !== "boolean")) {
    return undefined;
  }
  return {
    k: "updateProp",
    map,
    layer,
    prop,
    ...(frame === null ? {} : { frame }),
    ...(at === null ? {} : { at }),
    ...(flipX === undefined ? {} : { flipX }),
    ...(flipY === undefined ? {} : { flipY }),
    ...(sortPixels === undefined ? {} : { sortPixels }),
    ...(behaviour === undefined ? {} : { behaviour }),
    ...(keepPlacement === undefined ? {} : { keepPlacement }),
    ...(footprint === undefined ? {} : { footprint }),
  };
}

/**
 * Reads an intent that names one field of the world, a map, a layer or a group.
 *
 * @param fields The intent's fields.
 * @returns The intent, or undefined when it is not exactly one of these kinds.
 */
function fieldIntentOf(fields: Fields): Intent | undefined {
  const { map, layer, group, name } = fields;
  switch (fields["k"]) {
    case "renameMap":
      return hasOnly(fields, ["k", "map", "name"]) && isId(map) && isName(name) ? { k: "renameMap", map, name } : undefined;
    case "renameLayer": {
      const autoName = fields["autoName"];
      if (!hasOnly(fields, ["k", "map", "layer", "name"], ["autoName"]) || !isId(map) || !isId(layer) || !isName(name) || !(autoName === undefined || autoName === null || typeof autoName === "boolean")) {
        return undefined;
      }
      return { k: "renameLayer", map, layer, name, ...(autoName === undefined ? {} : { autoName }) };
    }
    case "renameGroup":
      return hasOnly(fields, ["k", "map", "group", "name"]) && isId(map) && isId(group) && isName(name) ? { k: "renameGroup", map, group, name } : undefined;
    case "setLayerGroup":
      return hasOnly(fields, ["k", "map", "layer", "group"]) && isId(map) && isId(layer) && isIdOrNull(group) ? { k: "setLayerGroup", map, layer, group } : undefined;
    case "setLayerTerrain": {
      const { terrain, variant, faceHeight } = fields;
      if (!hasOnly(fields, ["k", "map", "layer", "terrain", "variant"], ["name", "faceHeight"]) || !isId(map) || !isId(layer) || !isText(terrain, 1) || !isText(variant) || (name !== undefined && !isName(name)) || (faceHeight !== undefined && !isFaceHeight(faceHeight))) {
        return undefined;
      }
      return { k: "setLayerTerrain", map, layer, terrain, variant, ...(name === undefined ? {} : { name }), ...(faceHeight === undefined ? {} : { faceHeight }) };
    }
    case "setLayerFaceHeight": {
      const height = fields["height"];
      return hasOnly(fields, ["k", "map", "layer", "height"]) && isId(map) && isId(layer) && (height === null || isFaceHeight(height)) ? { k: "setLayerFaceHeight", map, layer, height } : undefined;
    }
    case "setTerrainName": {
      const entry = fields["entry"];
      return hasOnly(fields, ["k", "entry", "name"]) && isKey(entry) && (name === null || (isName(name) && name.trim() !== "")) ? { k: "setTerrainName", entry, name } : undefined;
    }
    case "setMapProperties": {
      const changes = hasOnly(fields, ["k", "map"], ["set", "remove"]) && isId(map) ? keyChangesOf(fields) : undefined;
      return changes === undefined || (changes.set === undefined && changes.remove === undefined) ? undefined : { k: "setMapProperties", map: map as string, ...changes };
    }
    case "setIssueIgnores": {
      const changes = hasOnly(fields, ["k", "map"], ["add", "remove"]) && isId(map) ? ignoreChangesOf(fields) : undefined;
      return changes === undefined || (changes.add === undefined && changes.remove === undefined) ? undefined : { k: "setIssueIgnores", map: map as string, ...changes };
    }
    default:
      return undefined;
  }
}

/**
 * Reads an intent over a prop or an entity.
 *
 * @param fields The intent's fields.
 * @returns The intent, or undefined when it is not exactly one of these kinds.
 */
function itemIntentOf(fields: Fields): Intent | undefined {
  const { map, layer, after } = fields;
  if (!isId(map) || !isId(layer)) {
    return undefined;
  }
  switch (fields["k"]) {
    case "updateProp":
      return updatePropOf(fields);
    case "updateEntity": {
      const entity = fields["entity"];
      const at = fields["at"] === undefined ? null : pointOf(fields["at"]);
      const changes = hasOnly(fields, ["k", "map", "layer", "entity"], ["at", "set", "remove"]) && isId(entity) ? keyChangesOf(fields) : undefined;
      if (changes === undefined || at === undefined || (at === null && changes.set === undefined && changes.remove === undefined)) {
        return undefined;
      }
      return { k: "updateEntity", map, layer, entity: entity as string, ...(at === null ? {} : { at }), ...changes };
    }
    case "addProp": {
      const prop = hasOnly(fields, ["k", "map", "layer", "prop", "after"]) && isIdOrNull(after) ? propOf(fields["prop"]) : undefined;
      return prop === undefined ? undefined : { k: "addProp", map, layer, prop, after: after as string | null };
    }
    case "addEntity": {
      const entity = hasOnly(fields, ["k", "map", "layer", "entity", "after"]) && isIdOrNull(after) ? entityOf(fields["entity"]) : undefined;
      return entity === undefined ? undefined : { k: "addEntity", map, layer, entity, after: after as string | null };
    }
    case "removeProp":
      return hasOnly(fields, ["k", "map", "layer", "prop"]) && isId(fields["prop"]) ? { k: "removeProp", map, layer, prop: fields["prop"] } : undefined;
    case "removeEntity": {
      const type = fields["type"];
      const read = hasOnly(fields, ["k", "map", "layer", "entity"], ["type"]) && isId(fields["entity"]) && (type === undefined || isText(type));
      return read ? { k: "removeEntity", map, layer, entity: fields["entity"] as string, ...(type === undefined ? {} : { type: type as string }) } : undefined;
    }
    default:
      return undefined;
  }
}

/**
 * Reads an intent over a map's structure or the world's: layers, groups, the band, maps and tilesets.
 *
 * @param fields The intent's fields.
 * @returns The intent, or undefined when it is not exactly one of these kinds.
 */
function structureIntentOf(fields: Fields): Intent | undefined {
  const { map, layer, group, above } = fields;
  switch (fields["k"]) {
    case "addLayer": {
      const made = group === undefined ? null : groupOf(group);
      const empty = hasOnly(fields, ["k", "map", "layer", "above"], ["group"]) && isId(map) && isIdOrNull(above) ? emptyLayerOf(layer) : undefined;
      if (empty === undefined || made === undefined || (made !== null && made.id !== empty.group)) {
        return undefined;
      }
      return { k: "addLayer", map: map as string, layer: empty, above: above as string | null, ...(made === null ? {} : { group: made }) };
    }
    case "removeLayer":
      return hasOnly(fields, ["k", "map", "layer"], ["group"]) && isId(map) && isId(layer) && (group === undefined || isId(group)) ? { k: "removeLayer", map, layer, ...(group === undefined ? {} : { group }) } : undefined;
    case "moveLayer": {
      const side = fields["side"];
      if (!hasOnly(fields, ["k", "map", "layer", "above"], ["side"]) || !isId(map) || !isId(layer) || !isIdOrNull(above) || above === layer || !(side === undefined || side === "ground" || side === "band")) {
        return undefined;
      }
      return { k: "moveLayer", map, layer, above, ...(side === undefined ? {} : { side }) };
    }
    case "setPlayersAbove": {
      const first = fields["first"];
      return hasOnly(fields, ["k", "map", "first"]) && isId(map) && isIdOrNull(first) ? { k: "setPlayersAbove", map, first } : undefined;
    }
    case "addGroup": {
      const made = hasOnly(fields, ["k", "map", "group"]) && isId(map) ? groupOf(group) : undefined;
      return made === undefined ? undefined : { k: "addGroup", map: map as string, group: made };
    }
    case "removeGroup":
      return hasOnly(fields, ["k", "map", "group"]) && isId(map) && isId(group) ? { k: "removeGroup", map, group } : undefined;
    case "addMap": {
      const after = fields["after"];
      const empty = hasOnly(fields, ["k", "map", "after"]) && isIdOrNull(after) ? emptyMapOf(map) : undefined;
      return empty === undefined ? undefined : { k: "addMap", map: empty, after: after as string | null };
    }
    case "removeMap":
      return hasOnly(fields, ["k", "map"]) && isId(map) ? { k: "removeMap", map } : undefined;
    case "changeTilesets":
      return changeTilesetsOf(fields);
    default:
      return undefined;
  }
}

/**
 * Reads one intent.
 *
 * @param value The value.
 * @returns The intent rebuilt with exactly its fields, or undefined when the value is not exactly one.
 */
function intentOf(value: unknown): Intent | undefined {
  const fields = recordOf(value);
  if (fields === undefined) {
    return undefined;
  }
  switch (fields["k"]) {
    case "cells":
      return patchOf(fields);
    case "renameMap":
    case "renameLayer":
    case "renameGroup":
    case "setLayerGroup":
    case "setLayerTerrain":
    case "setLayerFaceHeight":
    case "setTerrainName":
    case "setMapProperties":
    case "setIssueIgnores":
      return fieldIntentOf(fields);
    case "updateProp":
    case "updateEntity":
    case "addProp":
    case "addEntity":
    case "removeProp":
    case "removeEntity":
      return itemIntentOf(fields);
    case "placeMap":
    case "setGrid":
    case "setMapGroups":
    case "setMapGroup":
    case "setMapDraft":
    case "setMapHost":
    case "setMapShare":
    case "setMapCapacity":
    case "setMapAtmosphere":
    case "setSeam":
    case "removeSeam":
    case "setCrossing":
    case "removeCrossing":
      return layoutIntentOf(fields);
    case "addDoor":
    case "removeDoor":
    case "updateDoor":
      return doorIntentOf(fields);
    default:
      return structureIntentOf(fields);
  }
}

/**
 * Reads an operation from a value already parsed: what a tab built, or the fields of a frame.
 *
 * @param value The value.
 * @returns The operation rebuilt with exactly its fields, or null when the value is not exactly one: an id and
 *   a group of the form `<tag>-<n>` and 1 to `OP_INTENTS_MAX` intents, each exactly an intent, their patches
 *   covering at most `OP_CELLS_MAX` cells, and the operation's own encoding at most `OP_FRAME_MAX` code units,
 *   so an operation that is accepted is accepted again however it is written.
 */
export function opOf(value: unknown): LiveOp | null {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["id", "group", "list"]) || !isOpId(fields["id"]) || !isOpId(fields["group"])) {
    return null;
  }
  const list = fields["list"];
  if (!Array.isArray(list) || list.length === 0 || list.length > OP_INTENTS_MAX) {
    return null;
  }
  const intents: Intent[] = [];
  for (const item of list as readonly unknown[]) {
    const intent = intentOf(item);
    if (intent === undefined) {
      return null;
    }
    intents.push(intent);
  }
  const op: LiveOp = { id: fields["id"], group: fields["group"], list: intents };
  return cellsOf(intents) <= OP_CELLS_MAX && encodeOp(op).length <= OP_FRAME_MAX ? op : null;
}

/**
 * How many cells the patches of an operation cover together.
 *
 * @param intents The operation's intents.
 * @returns The count.
 */
export function cellsOf(intents: readonly Intent[]): number {
  let count = 0;
  for (const intent of intents) {
    if (intent.k === "cells") {
      for (const run of intent.cells) {
        count += run[1];
      }
    }
  }
  return count;
}

/**
 * Reads an operation frame.
 *
 * @param text The frame.
 * @returns The operation, or null when the frame is longer than `OP_FRAME_MAX`, not JSON or not exactly an
 *   operation.
 */
export function parseOp(text: string): LiveOp | null {
  if (text.length > OP_FRAME_MAX) {
    return null;
  }
  try {
    return opOf(JSON.parse(text) as unknown);
  } catch {
    return null;
  }
}

/**
 * Writes an operation as its frame.
 *
 * @param op The operation.
 * @returns The frame.
 */
export function encodeOp(op: LiveOp): string {
  return JSON.stringify(op);
}
